/**
 * BlindSpotsAppendix (RV pack, RV6) — "What this report does not
 * know" appendix subsection. Renders in EVERY report unconditionally
 * so the platform is openly honest about what it can't see; compare to
 * today, where gaps get buried inside the Data Gaps box readers skim.
 *
 * When there are no surfacing gaps:
 *   > All data sources reporting normally. Full evidence trail in
 *   > §11 Appendix.
 *
 * When there are gaps, one row per gap:
 *   <field>
 *     Why missing: <reason>
 *     Severity:    <effective impact tier>
 *     Suggested supplement: <recommendation>
 *
 * Wires through RV-A's per-section impact (intent-weighted) once that
 * lands; until then it uses the local `effectiveGapImpact` stub from
 * src/lib/gapImpact.ts.
 */
import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { DataGap } from '../../../types/siteEvidence';
import type { ReportTheme } from '../../../types/reportTheme';
import { EditableHeading } from './EditableHeading';
import type { EffectiveGapImpact } from '../../../lib/gapImpact';
import { resolveReportBlindSpots } from '../../../lib/resolveReportBlindSpots';
import { roleColor, type ReportColorRole } from '../../../lib/reportRoleColors';

const MONO = 'var(--report-body-font, inherit)';

interface Props {
  report: SiteRagReport;
  theme: ReportTheme;
  isEditing: boolean;
  headingOverrides: Record<string, string>;
  setHeading: (key: string, value: string) => void;
  /** When true, omit outer chrome — parent EditorialSectionShell carries padding + data-section. */
  embedded?: boolean;
  /** Optional callback when the reader clicks a remediation chip
   *  (e.g. "Upload supplement"). When omitted, chips render as
   *  informational labels only. */
  onRemediate?: (gap: DataGap) => void;
  /** V2 artifacts omit an empty optional section instead of inventing an
   *  all-clear claim that is absent from the immutable document model. */
  suppressWhenEmpty?: boolean;
}

// Severity → editorial ROLE. critical → negative, significant →
// warning, noted → muted, irrelevant → faint. The label ink reads
// roleColor(theme, role) so the severity follows the active preset's
// status palette rather than literal rose/amber.
const IMPACT_COPY: Record<EffectiveGapImpact, { label: string; role: ReportColorRole }> = {
  critical:    { label: 'CRITICAL',    role: 'negative' },
  significant: { label: 'SIGNIFICANT', role: 'warning' },
  noted:       { label: 'NOTED',       role: 'muted' },
  irrelevant:  { label: 'IRRELEVANT',  role: 'faint' },
};

export const BlindSpotsAppendix: React.FC<Props> = ({
  report, theme, isEditing, headingOverrides, setHeading, embedded = false, onRemediate,
  suppressWhenEmpty = false,
}) => {
  const surfacing = React.useMemo(() => resolveReportBlindSpots(report), [report]);

  if (suppressWhenEmpty && surfacing.length === 0) return null;

  const heading = (
    <h3
      className="text-sm font-bold uppercase tracking-widest"
      style={{ fontFamily: MONO, letterSpacing: '0.16em', color: roleColor(theme, 'accent') }}
    >
      <EditableHeading
        headingKey="blindSpots"
        defaultText="What this report does not know"
        className="text-sm font-bold uppercase tracking-widest"
        isEditing={isEditing}
        overrides={headingOverrides}
        onChange={setHeading}
      />
    </h3>
  );

  const body = surfacing.length === 0 ? (
    <p className="text-xs leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
      All data sources reporting normally. Full evidence trail in the Appendix above.
    </p>
  ) : (
    <>
      <p className="text-[11px] leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
        The following data could not be retrieved or fell outside the system's coverage.
        For decisions in these areas, supplement this report with the noted sources.
      </p>
      <ul className="space-y-3 text-xs">
        {surfacing.map(({ gap, impact }, i) => {
          const copy = IMPACT_COPY[impact];
          return (
            <li
              key={`${gap.field}-${i}`}
              data-testid={`blind-spot-row-${impact}`}
              className="py-1 space-y-1"
              // William QA 2026-06-28 — left accent rule removed report-wide
            >
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>{gap.field}</span>
                <span
                  className="text-[9px] font-bold tracking-widest"
                  style={{ fontFamily: MONO, color: roleColor(theme, copy.role) }}
                >
                  {copy.label}
                </span>
                {gap.providerId && (
                  <span className="text-[10px]" style={{ fontFamily: MONO, color: roleColor(theme, 'faint') }}>
                    ({gap.providerId})
                  </span>
                )}
              </div>
              {gap.reason && (
                <div className="text-[11px] leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
                  <span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>Why missing:</span>{' '}
                  {gap.reason}
                </div>
              )}
              {gap.recommendation && (
                <div className="text-[11px] leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
                  <span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>Suggested supplement:</span>{' '}
                  {gap.recommendation}
                </div>
              )}
              {onRemediate && (
                <button
                  type="button"
                  onClick={() => onRemediate(gap)}
                  className="text-[10px] font-bold uppercase tracking-widest hover:underline"
                  style={{ fontFamily: MONO, color: roleColor(theme, 'accent') }}
                >
                  Upload supplement →
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-3" data-testid="blind-spots-appendix">
        {heading}
        {body}
      </div>
    );
  }

  return (
    <div
      data-section="blindSpots"
      data-paged-forward-fold="true"
      data-testid="blind-spots-appendix"
      className="space-y-3 pt-8"
      style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
    >
      {heading}
      {body}
    </div>
  );
};
